// Content linking — a News item can be linked to another content so its info
// (date, time, location, action) is pulled LIVE from the single source of truth.
import { galleryCover } from "./media";

export const LINK_TYPES = [
  { value: "", label: "Aucun (actualité informative)" },
  { value: "formation", label: "Formation" },
  { value: "event", label: "Événement (agenda)" },
  { value: "festival", label: "Festival" },
  { value: "project", label: "Projet / Tournage" },
  { value: "video", label: "Vidéo" },
  { value: "podcast", label: "Podcast (bientôt)" },
];

export const getLinkableItems = (content = {}, type) => {
  switch (type) {
    case "formation": return (content.formations?.items || []).map((it) => ({ id: it.id, label: it.title || "(Sans titre)" }));
    case "event": return (content.events?.items || []).map((it) => ({ id: it.id, label: it.title || "(Sans titre)" }));
    case "project": return (content.realisations || []).map((it) => ({ id: it.id, label: it.title || "(Sans titre)" }));
    case "video": return (content.videos?.items || []).map((it) => ({ id: it.id, label: it.title || "(Sans titre)" }));
    case "festival": return [{ id: "festival", label: `${content.festival?.titlePrefix || "Karams"} ${content.festival?.titleAccent || "Festival"}`.trim() }];
    default: return [];
  }
};

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
export const formatFrDate = (v) => {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
  if (!m) return v; // already free text
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d)) return v;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// End datetime (ISO, end of day) of the content a News item is linked to — mirrors backend
// `_linked_end_iso`. Lets Admin preview reflect the automatic "past" transition.
const endOfDayIso = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  return m ? `${m[0]}T23:59:59+00:00` : "";
};
export const linkedEndIso = (content = {}, it = {}) => {
  const { linkedType: t, linkedId: id } = it;
  if (t === "formation") {
    const f = (content.formations?.items || []).find((x) => x.id === id);
    const dates = (f?.sessions || []).map((s) => s.date).filter(Boolean);
    return dates.length ? endOfDayIso(dates.sort().slice(-1)[0]) : "";
  }
  if (t === "event") {
    const e = (content.events?.items || []).find((x) => x.id === id);
    return e ? endOfDayIso(e.endDate || e.date) : "";
  }
  return "";
};

// Returns a normalized descriptor of the linked content, read live from `content`.
export const resolveLinked = (content = {}, type, id) => {
  if (!type) return null;
  if (type === "formation") {
    const it = (content.formations?.items || []).find((x) => x.id === id);
    if (!it) return null;
    const s = (it.sessions || [])[0] || {};
    return { kind: "formation", target: it, title: it.title,
      image: it.mediaType === "slideshow" ? galleryCover(it.gallery) : it.image,
      dateText: formatFrDate(s.date), timeText: s.time || "", locationName: s.location || "", address: s.address || "",
      href: `/formation/${it.slug || it.id}`, ctaLabel: "S'inscrire" };
  }
  if (type === "event") {
    const it = (content.events?.items || []).find((x) => x.id === id);
    if (!it) return null;
    const dates = [formatFrDate(it.date), it.endDate ? formatFrDate(it.endDate) : ""].filter(Boolean).join(" → ");
    return { kind: "event", target: it, title: it.title, image: it.image || "",
      dateText: dates, timeText: it.time || "", locationName: it.location || "", address: it.address || "",
      href: `/calendrier`, ctaLabel: "Participer" };
  }
  if (type === "festival") {
    const f = content.festival || {};
    return { kind: "festival", target: f, title: `${f.titlePrefix || ""} ${f.titleAccent || ""}`.trim() || "Festival",
      image: f.poster || f.backgroundImage || "", dateText: f.dates || "", timeText: "",
      locationName: f.place || "", address: f.address || "", href: `/festival`, ctaLabel: f.ctaLabel || "Participer au festival" };
  }
  if (type === "project") {
    const it = (content.realisations || []).find((x) => x.id === id);
    if (!it) return null;
    return { kind: "project", target: it, title: it.title, image: it.img || it.image || "",
      dateText: it.info?.year || "", timeText: "", locationName: it.info?.location || "", address: "",
      href: `/projet/${it.slug || it.id}`, ctaLabel: "Découvrir le projet" };
  }
  if (type === "video") {
    const it = (content.videos?.items || []).find((x) => x.id === id);
    if (!it) return null;
    return { kind: "video", target: it, title: it.title, image: it.thumbnail || it.poster || "",
      dateText: "", timeText: "", locationName: "", address: "", href: `/videos`, ctaLabel: "Regarder" };
  }
  return null;
};
